import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import {
  User,
  UserInput,
  UserStatus,
  OnboardingStatus,
} from '@core/generated/gql-types';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useOnboardingStore } from '@core/onboarding/useOnboardingStore';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import EditableSection from '@/components/shared/EditableSection';
import {
  actionFieldInputBlockClassName,
  actionFieldInputClassName,
  actionPrimaryButtonClassName,
  actionTextClassName,
} from '@core/theme/actionTheme.util';
import StatusDropdown from '@/modules/workers/components/StatusDropdown';
import {
  formatDobForInput,
  formatDobForApi,
  formatDobForDisplay,
  getCustomAttribute,
} from '@/utils/utils';
import AssignedAdminDropdown from './AssignAdminDropdown';

interface CandidateDetailsProps {
  worker: User;
}

const CandidateDetails: React.FC<CandidateDetailsProps> = ({ worker }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { fetchDetailedWorkerById, updateWorkerStatus, updateWorker } =
    useWorkerStore();
  const { isLoading: tenantUsersLoading, fetchTenantUsersForTenant } =
    useTenantStore();
  const { getOnboardingForUser } = useOnboardingStore();
  const { getTerm } = useTemplateStore();

  useEffect(() => {
    if (worker.tenantId) {
      fetchTenantUsersForTenant(worker.tenantId);
    }
  }, [worker.tenantId, fetchTenantUsersForTenant]);

  // Get onboarding status to validate status changes
  const onboarding = getOnboardingForUser(worker.id);
  const isOnboardingComplete =
    onboarding?.status === OnboardingStatus.Completed;

  const atLeastOneClassificationLevel = worker.classificationLevels?.length > 0;

  const initialValues = {
    firstName: worker.firstName || '',
    lastName: worker.lastName || '',
    email: worker.email || '',
    dob: formatDobForInput(worker.dob, worker.address),
    phoneNumber: worker.phoneNumber || '',
    address: worker.address || {
      addressLine1: '',
      addressLine2: '',
      suburb: '',
      state: '',
      postcode: '',
      countryLabel: '',
      countryValue: '',
    },
    onboardDate: getCustomAttribute(worker, 'onboardDate') || '',
    onboardedBy: getCustomAttribute(worker, 'onboardedBy') || '',
    idealRadius: worker.idealRadius != null ? String(worker.idealRadius) : '',
  };

  const handleStatusChange = async (newStatus: UserStatus) => {
    if (isUpdatingStatus) return;

    // Prevent setting status to Active if onboarding is not complete
    if (newStatus === UserStatus.Active && !isOnboardingComplete) {
      toast.error('Cannot set status to Active until onboarding is complete', {
        position: 'bottom-left',
        autoClose: 5000,
      });
      return;
    }

    // Classification level is required for Active status.
    // No classification level means the worker is not qualified for any jobs.
    if (newStatus === UserStatus.Active && !atLeastOneClassificationLevel) {
      toast.error(
        'Cannot set status to Active until at least one classification level is assigned',
        {
          position: 'bottom-left',
          autoClose: 5000,
        },
      );
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await updateWorkerStatus(worker.id, newStatus);
    } catch (error) {
      console.error(`Error updating status: ${error}`);
      toast.error(
        `Update status failed: ${error instanceof Error ? error.message : 'Error'}`,
        {
          position: 'bottom-left',
          autoClose: 5000,
          toastId: 'update-status-error',
        },
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSave = async (values: typeof initialValues) => {
    const idealRadiusTrimmed = values.idealRadius.trim();
    let idealRadiusParsed: number | undefined;
    if (idealRadiusTrimmed !== '') {
      if (!/^\d+$/.test(idealRadiusTrimmed)) {
        toast.error(
          'Ideal radius must be a whole number (kilometres), or left blank to keep the current value',
          {
            position: 'bottom-left',
            autoClose: 5000,
          },
        );
        return;
      }
      idealRadiusParsed = Number(idealRadiusTrimmed);
    }

    setIsSaving(true);
    try {
      const customAttributes = [
        { ref: 'onboardDate', value: values.onboardDate },
        { ref: 'onboardedBy', value: values.onboardedBy },
      ].filter((attr) => attr.value !== undefined && attr.value !== '');

      const userInput: UserInput = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        dob: formatDobForApi(values.dob),
        ...(idealRadiusParsed !== undefined
          ? { idealRadius: idealRadiusParsed }
          : {}),
        address: values.address
          ? {
              addressLine1: values.address.addressLine1 || '',
              addressLine2: values.address.addressLine2 || '',
              suburb: values.address.suburb || '',
              state: values.address.state || '',
              postcode: values.address.postcode || '',
              countryLabel: values.address.countryLabel || '',
              countryValue: values.address.countryValue || '',
            }
          : {
              addressLine1: '',
              addressLine2: '',
              suburb: '',
              state: '',
              postcode: '',
              countryLabel: '',
              countryValue: '',
            },
        customAttributes,
      };

      await updateWorker(worker.id, userInput);

      await fetchDetailedWorkerById(worker.id);

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating candidate details:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Full-screen loading overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-50 flex min-h-[100dvh] min-h-screen items-center justify-center bg-black/30">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-8 py-6 shadow-xl">
            <svg
              className={`h-10 w-10 animate-spin ${actionTextClassName}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">Saving...</span>
          </div>
        </div>
      )}

      <EditableSection
        title={getTerm(TemplateTerm.WORKER, false)}
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onCancel={() => setIsEditing(false)}
        showSaveButton={false}
        collapsible
      >
        {!isEditing ? (
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 px-2 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6 sm:px-4 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-6 lg:px-0">
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">First Name</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {worker.firstName || '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">Last Name</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {worker.lastName || '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm break-words text-gray-900">
                {worker.email || '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">
                Date of Birth
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDobForDisplay(worker.dob, worker.address)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">
                Phone Number 1
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {worker.phoneNumber || '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">
                Ideal radius (km)
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {worker.idealRadius != null ? String(worker.idealRadius) : '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-sm break-words text-gray-900">
                {generateFriendlyAddress(worker.address || {}) || '-'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <StatusDropdown
                  value={worker.status}
                  onChange={handleStatusChange}
                  disabled={isUpdatingStatus}
                />
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-sm font-medium text-gray-500">
                Assigned admin
              </dt>
              <dd className="mt-1 min-w-0">
                <AssignedAdminDropdown
                  worker={worker}
                  loading={tenantUsersLoading}
                />
              </dd>
            </div>
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSave}
            enableReinitialize
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-x-4 gap-y-6 px-2 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6 sm:px-4 lg:gap-x-12 lg:px-0">
                  {/* Left Column */}
                  <div className="space-y-4 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        First Name
                      </label>
                      <Field
                        name="firstName"
                        type="text"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Last Name
                      </label>
                      <Field
                        name="lastName"
                        type="text"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Email
                      </label>
                      <Field
                        name="email"
                        type="email"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Date of Birth
                      </label>
                      <Field
                        name="dob"
                        type="date"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Phone Number 1
                      </label>
                      <Field
                        name="phoneNumber"
                        type="text"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Ideal radius (km)
                      </label>
                      <Field
                        name="idealRadius"
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 20"
                        className={actionFieldInputBlockClassName}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        Address
                      </label>
                      <Field
                        name="address.addressLine1"
                        type="text"
                        placeholder="Address Line 1"
                        className={actionFieldInputBlockClassName}
                      />
                      <Field
                        name="address.addressLine2"
                        type="text"
                        placeholder="Address Line 2"
                        className={actionFieldInputBlockClassName}
                      />
                      <Field
                        name="address.suburb"
                        type="text"
                        placeholder="Suburb"
                        className={`mt-2 block w-full ${actionFieldInputClassName}`}
                      />
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Field
                          name="address.state"
                          type="text"
                          placeholder="State"
                          className={`block w-full ${actionFieldInputClassName}`}
                        />
                        <Field
                          name="address.postcode"
                          type="text"
                          placeholder="Postcode"
                          className={`block w-full ${actionFieldInputClassName}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4 sm:space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Status
                      </dt>
                      <dd className="mt-1">
                        <StatusDropdown
                          value={worker.status}
                          onChange={handleStatusChange}
                          disabled={isUpdatingStatus}
                        />
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-500">
                        Assigned admin
                      </label>
                      <div className="mt-1 min-w-0">
                        <AssignedAdminDropdown
                          worker={worker}
                          loading={tenantUsersLoading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex flex-col-reverse gap-3 px-2 sm:flex-row sm:justify-end sm:px-0">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    disabled={isSaving}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </EditableSection>
    </>
  );
};

export default CandidateDetails;
